import { DateTime } from 'luxon';
import type { CalendarAttendee, CalendarEvent } from '~/src/types/api';
import { defaultColors, outlookPresetColors } from '../constants/calendarEventConstants';

function parseWithOptionalZone(iso: string, timeZone?: string | null) {
  if (!timeZone) {
    return DateTime.fromISO(iso);
  }

  const zoned = DateTime.fromISO(iso, { zone: timeZone });
  if (zoned.isValid) {
    return zoned;
  }

  // Some providers may return unsupported zone labels (e.g. Windows zones).
  // Fall back to parsing without explicit zone instead of dropping the event.
  return DateTime.fromISO(iso);
}

export function getOrganizerEmail(event: CalendarEvent): string {
  // Microsoft Graph format
  if (event.organizer?.emailAddress) {
    return event.organizer.emailAddress.address ?? '';
  }
  // Google Calendar format
  if (event.organizer?.email) {
    return event.organizer.email;
  }
  return '';
}

/** Normalize Graph `webLink` onto `htmlLink` for shared edit-redirect handling. */
export function withMicrosoftProvider<T extends { htmlLink?: string; webLink?: string }>(
  event: T,
): T & { provider: 'microsoft'; htmlLink?: string } {
  const htmlLink = event.htmlLink ?? event.webLink;
  return {
    ...event,
    provider: 'microsoft',
    ...(htmlLink ? { htmlLink } : {}),
  };
}

export function getAttendeeEmails(event: CalendarEvent): string[] {
  if (!event.attendees) {
    return [];
  }

  return event.attendees.map((a) => a.emailAddress?.address ?? a.email ?? '');
}

/** Get YYYY-MM-DD in local timezone for grouping and comparison */
export function getLocalDateKey(dateInput?: string, timeZone?: string): string {
  if (!dateInput) {
    return '';
  }

  const base = parseWithOptionalZone(dateInput, timeZone);
  if (!base.isValid) {
    return '';
  }

  return base.toLocal().toFormat('yyyy-MM-dd');
}

export function toLocalDateTime(
  iso: string | undefined | null,
  timeZone?: string | null,
  invalidReason = 'Missing date',
) {
  if (!iso) {
    return DateTime.invalid(invalidReason);
  }
  const parsed = parseWithOptionalZone(iso, timeZone);
  return parsed.isValid ? parsed.toLocal() : DateTime.invalid(invalidReason);
}

export function getEventLocalStart(event: CalendarEvent) {
  const startDateTime = event.start?.dateTime ?? event.start?.date;
  const eventTimeZone = event.start?.timeZone;
  return toLocalDateTime(startDateTime, eventTimeZone, 'Missing start date');
}

export function getOrganizerOrAttendee(organizer: CalendarAttendee | null): string {
  if (!organizer) {
    return '';
  }

  const getInitials = (value?: string): string => {
    if (!value) {
      return '';
    }

    const parts = value
      .trim()
      .split(/\s+/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (!parts.length) {
      return '';
    }

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  // Microsoft Graph format
  if (organizer.emailAddress) {
    const name = organizer.emailAddress.name || organizer.emailAddress.address?.split('@')[0];
    return getInitials(name);
  }

  // Google Calendar format
  const name = organizer.displayName || organizer.email?.split('@')[0];
  return getInitials(name);
}

export function getAttendeeImage(attendee: CalendarAttendee): string | null {
  return attendee?.photoUrl || attendee?.photo || attendee?.avatar || attendee?.picture || null;
}

export function getLocationLink(event: CalendarEvent): string | null {
  if (!event?.location) {
    return null;
  }

  const loc = event.location;
  const location = typeof loc === 'string' ? loc : loc?.displayName || loc?.uniqueId || null;

  if (typeof location === 'string' && /^https?:\/\//i.test(location)) {
    return location;
  }

  return null;
}

type GoogleColors = {
  event?: Record<string, { background?: string }>;
  calendar?: Record<string, { background?: string }>;
};

type MicrosoftCategory = {
  displayName?: string;
  color?: string;
};

type EventColors = { backgroundColor?: string; borderLeftColor?: string };

function mapColorToStyles(color: string): EventColors {
  return {
    backgroundColor: `${color}1a`,
    borderLeftColor: color,
  };
}

function getExplicitEventColorHex(
  event: CalendarEvent | null | undefined,
  googleColors?: GoogleColors | null,
  microsoftCategories?: MicrosoftCategory[] | null,
): string | undefined {
  if (!event) {
    return undefined;
  }

  if (event.provider === 'google') {
    const colorId = event?.colorId as string | undefined;
    const eventColors = googleColors?.event ?? {};
    const calendarColors = googleColors?.calendar ?? {};

    const apiBackgroundColor =
      (colorId && eventColors[colorId]?.background) ||
      (event.calendarId && calendarColors[event.calendarId]?.background);

    return event.backgroundColor || apiBackgroundColor;
  }

  if (event.provider === 'microsoft') {
    const category = Array.isArray(event?.categories) ? event.categories[0] : undefined;
    if (!category) {
      return undefined;
    }

    const masterCategories = microsoftCategories ?? [];
    const matchedCategory = masterCategories.find((c) => c.displayName === category);
    const presetKey = matchedCategory?.color as string | undefined;

    return presetKey ? outlookPresetColors[presetKey] : undefined;
  }

  return undefined;
}

export function hasExplicitEventColor(
  event: CalendarEvent | null | undefined,
  googleColors?: GoogleColors | null,
  microsoftCategories?: MicrosoftCategory[] | null,
): boolean {
  return !!getExplicitEventColorHex(event, googleColors, microsoftCategories);
}

export function getEventColors(
  event: CalendarEvent | null | undefined,
  googleColors?: GoogleColors | null,
  microsoftCategories?: MicrosoftCategory[] | null,
  fallbackColors?: EventColors,
): EventColors {
  if (!event) {
    return fallbackColors ?? defaultColors;
  }

  const explicitColorHex = getExplicitEventColorHex(event, googleColors, microsoftCategories);
  if (explicitColorHex) {
    return mapColorToStyles(explicitColorHex);
  }

  return fallbackColors ?? defaultColors;
}

export function filterEventsByTime(
  events: CalendarEvent[],
  activeTab: 'upcoming' | 'past',
): CalendarEvent[] {
  const now = DateTime.local();

  const isUpcomingTab = activeTab === 'upcoming';

  return events.filter((event) => {
    const startIso = event.start?.dateTime ?? event.start?.date;
    const endIso = event.end?.dateTime ?? event.end?.date;

    const eventTimeZone = event.start?.timeZone ?? event.end?.timeZone ?? undefined;

    const eventStart = toLocalDateTime(startIso, eventTimeZone);
    const eventEnd = endIso ? toLocalDateTime(endIso, eventTimeZone) : eventStart;

    if (!(eventStart.isValid || eventEnd.isValid)) {
      return false;
    }

    // Use end time for deciding past vs upcoming so ongoing events stay in upcoming
    if (isUpcomingTab) {
      return eventEnd >= now;
    }

    return eventEnd < now;
  });
}
